import { useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  paymentMethodQueryKey,
  useCustomerPaymentMethod,
} from "@/api/hooks/customer.hooks";
import Button from "@/components/ui/Button";
import { useToast } from "@/hooks/use-toast";
import { CardCollector, type CardCollectorHandle } from "./CardCollector";
import { SavedCardSummary } from "./SavedCardSummary";

/**
 * Self-contained "Payment Method" panel for the profile page: shows the saved
 * card and lets the patient add or replace it. Saving a new card detaches the
 * old one on the backend, so exactly one card is ever on file.
 */
export function ManagePaymentMethod() {
  const { data: card, isLoading } = useCustomerPaymentMethod({ enabled: true });
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const collectorRef = useRef<CardCollectorHandle>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const hasCard = card?.hasPaymentMethod ?? false;

  const handleSave = async () => {
    setError(null);
    setIsSaving(true);
    const result = await collectorRef.current?.confirm();
    setIsSaving(false);

    if (!result?.ok) {
      setError(result?.error ?? "We couldn't save your card.");
      return;
    }

    await queryClient.invalidateQueries({ queryKey: paymentMethodQueryKey });
    setIsAdding(false);
    toast({
      title: "Card saved",
      description: "Your payment method is ready for future appointments.",
    });
  };

  if (isLoading) {
    return <div className="h-20 rounded-[0.625rem] bg-muted animate-pulse" />;
  }

  return (
    <div className="space-y-4">
      {hasCard && card ? (
        <SavedCardSummary card={card} />
      ) : (
        !isAdding && (
          <p className="text-sm text-muted-foreground">
            No card on file. A saved card is required to book an appointment.
          </p>
        )
      )}

      {isAdding ? (
        <div className="space-y-4">
          <CardCollector ref={collectorRef} onError={setError} />
          {error && <p className="text-sm text-destructive">{error}</p>}
          <div className="flex justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setIsAdding(false);
                setError(null);
              }}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button type="button" onClick={handleSave} disabled={isSaving}>
              {isSaving ? "Saving..." : "Save card"}
            </Button>
          </div>
        </div>
      ) : (
        <Button
          type="button"
          variant={hasCard ? "outline" : "default"}
          onClick={() => setIsAdding(true)}
        >
          {hasCard ? "Replace card" : "Add card"}
        </Button>
      )}
    </div>
  );
}
